import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Zap, RefreshCw, Plug, Battery, TrendingUp,
  AlertCircle, CheckCircle2, Loader2, Trash2, Smartphone, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from "recharts";

// --- Helpers ---
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
};

const fmtKwh = (n) => (n == null ? "—" : `${Number(n).toFixed(2)} kWh`);
const fmtKw = (n) => (n == null ? "—" : `${Number(n).toFixed(1)} kW`);

const statusLabel = (s) => {
  const map = {
    Available: "Ledig",
    Preparing: "Förbereder",
    Charging: "Laddar",
    SuspendedEVSE: "Väntar (laddare)",
    SuspendedEV: "Väntar (bil)",
    Finishing: "Avslutar",
    Reserved: "Reserverad",
    Unavailable: "Otillgänglig",
    Faulted: "Fel",
  };
  return map[s] || s || "—";
};

const statusColor = (s) => {
  if (s === "Charging") return "text-[color:var(--sunset)]";
  if (s === "Available") return "text-[color:var(--moss)]";
  if (s === "Faulted" || s === "Unavailable") return "text-[color:var(--danger,#DC2626)]";
  return "text-[color:var(--text-secondary)]";
};

// --- Setup-wizard ---
function DefaSetup({ onDone }) {
  const [step, setStep] = useState("phone"); // phone → code → done
  const [mode, setMode] = useState("sms"); // sms | manual
  const [phone, setPhone] = useState("+46");
  const [code, setCode] = useState("");
  const [userId, setUserId] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const sendSms = async () => {
    if (!/^\+\d{6,15}$/.test(phone)) {
      toast.error("Ogiltigt format — använd +46701234567");
      return;
    }
    setBusy(true);
    try {
      await api.post("/defa/setup/sms", { phone_number: phone });
      toast.success("SMS-kod skickad! Kolla din telefon.");
      setStep("code");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skicka SMS");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (!code) return;
    setBusy(true);
    try {
      await api.post("/defa/setup/verify", { phone_number: phone, code });
      toast.success("Ansluten till DEFA Power!");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fel kod");
    } finally {
      setBusy(false);
    }
  };

  const manualSave = async () => {
    if (!userId || !token) return;
    setBusy(true);
    try {
      await api.post("/defa/setup/manual", { user_id: userId, token });
      toast.success("DEFA-credentials sparade!");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ogiltiga credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="defa-setup">
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={mode === "sms" ? "default" : "outline"}
          onClick={() => setMode("sms")}
          data-testid="defa-mode-sms"
        >
          <Smartphone className="w-4 h-4 mr-1" /> SMS-kod
        </Button>
        <Button
          size="sm"
          variant={mode === "manual" ? "default" : "outline"}
          onClick={() => setMode("manual")}
          data-testid="defa-mode-manual"
        >
          User-ID + Token
        </Button>
      </div>

      {mode === "sms" && step === "phone" && (
        <>
          <label className="text-sm text-[color:var(--text-secondary)]">
            Telefonnummer (samma som i DEFA Power-appen)
          </label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+46701234567"
            data-testid="defa-phone-input"
          />
          <Button
            onClick={sendSms}
            disabled={busy}
            className="w-full"
            data-testid="defa-send-sms-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
            Skicka SMS-kod
          </Button>
          <p className="text-xs text-[color:var(--text-secondary)]">
            Detta använder en oberoende dev-token så du inte loggas ut från din egen DEFA-app.
          </p>
        </>
      )}

      {mode === "sms" && step === "code" && (
        <>
          <label className="text-sm text-[color:var(--text-secondary)]">
            SMS-kod från DEFA
          </label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            data-testid="defa-code-input"
            autoFocus
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep("phone")}>Tillbaka</Button>
            <Button
              onClick={verifyCode}
              disabled={busy || !code}
              className="flex-1"
              data-testid="defa-verify-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Verifiera kod
            </Button>
          </div>
        </>
      )}

      {mode === "manual" && (
        <>
          <label className="text-sm text-[color:var(--text-secondary)]">
            User-ID (från DEFA Power-appen &raquo; Inställningar &raquo; Om)
          </label>
          <Input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="ex. 12345"
            data-testid="defa-userid-input"
          />
          <label className="text-sm text-[color:var(--text-secondary)]">
            API-token
          </label>
          <Input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Bearer-token"
            type="password"
            data-testid="defa-token-input"
          />
          <Button
            onClick={manualSave}
            disabled={busy || !userId || !token}
            className="w-full"
            data-testid="defa-manual-save-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Spara & anslut
          </Button>
        </>
      )}
    </div>
  );
}

// --- Charger-kort ---
function ChargerCard({ charger }) {
  return (
    <div
      className="p-5 rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)] space-y-3"
      data-testid={`defa-charger-${charger.chargepoint_id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-lg font-semibold flex items-center gap-2">
            <Plug className="w-5 h-5 text-[color:var(--sunset)]" />
            {charger.display_name || "DEFA-laddare"}
          </div>
          {charger.location && (
            <div className="text-xs text-[color:var(--text-secondary)]">{charger.location}</div>
          )}
        </div>
      </div>

      {charger.error && (
        <div className="flex items-center gap-2 text-sm text-[color:var(--danger,#DC2626)]">
          <AlertCircle className="w-4 h-4" /> {charger.error}
        </div>
      )}

      {(charger.connectors || []).map((c) => (
        <div
          key={c.connector_id}
          className="border-t border-[color:var(--border)] pt-3 space-y-2"
          data-testid={`defa-connector-${c.connector_id}`}
        >
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">
              {c.display_name || c.alias}
            </div>
            <div className={`text-sm font-semibold ${statusColor(c.live?.ocpp_status || c.status)}`}>
              {statusLabel(c.live?.ocpp_status || c.status)}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-secondary)]">Effekt nu</div>
              <div className="text-lg font-semibold">{fmtKw(c.live?.power_consumption)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-secondary)]">Session</div>
              <div className="text-lg font-semibold">{fmtKwh(c.live?.transaction_kwh)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-secondary)]">Totalt</div>
              <div className="text-lg font-semibold">{fmtKwh(c.live?.meter_value ?? c.meter_kwh)}</div>
            </div>
          </div>

          <div className="text-xs text-[color:var(--text-secondary)] flex flex-wrap gap-x-3 gap-y-1">
            {c.model && <span>{c.model}</span>}
            {c.max_current && <span>{c.max_current}A max</span>}
            {c.firmware_version && <span>FW {c.firmware_version}</span>}
            {c.serial_number && <span className="font-mono">{c.serial_number}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- History-vy med graf ---
function HistorySection() {
  const [weekly, setWeekly] = useState(null);
  const [daily, setDaily] = useState(null);
  const [view, setView] = useState("weekly"); // weekly | daily
  const [range, setRange] = useState(12);       // veckor eller dagar
  const [loading, setLoading] = useState(false);

  const load = async (nextView = view, nextRange = range) => {
    setLoading(true);
    try {
      if (nextView === "weekly") {
        const r = await api.get(`/defa/history/weekly?weeks=${nextRange}`);
        setWeekly(r.data);
      } else {
        const r = await api.get(`/defa/history/daily?days=${nextRange}`);
        setDaily(r.data);
      }
    } catch (e) {
      toast.error("Kunde inte hämta historik");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const setViewAndLoad = (v) => {
    const defaultRange = v === "weekly" ? 12 : 30;
    setView(v);
    setRange(defaultRange);
    load(v, defaultRange);
  };

  const setRangeAndLoad = (r) => {
    setRange(r);
    load(view, r);
  };

  const chartData = view === "weekly"
    ? (weekly?.weekly || []).map((w) => ({ x: w.label, kwh: w.kwh, key: w.week }))
    : (daily?.daily || []).map((d) => ({
        x: d.date.slice(5),  // MM-DD
        kwh: d.kwh, key: d.date,
      }));

  const total = view === "weekly" ? weekly?.total_kwh : daily?.total_kwh;
  const avg = view === "weekly"
    ? weekly?.average_kwh_per_week
    : daily?.average_kwh_per_day;
  const avgLabel = view === "weekly" ? "Snitt / vecka" : "Snitt / dag";
  const rangeOptions = view === "weekly" ? [4, 12, 26, 52] : [7, 30, 90];

  const maxKwh = Math.max(...chartData.map((d) => d.kwh), 0);

  return (
    <div className="space-y-4" data-testid="defa-history-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Förbrukning
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={view === "weekly" ? "default" : "outline"}
            onClick={() => setViewAndLoad("weekly")}
            data-testid="defa-view-weekly"
          >
            Per vecka
          </Button>
          <Button
            size="sm"
            variant={view === "daily" ? "default" : "outline"}
            onClick={() => setViewAndLoad("daily")}
            data-testid="defa-view-daily"
          >
            Per dag
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {rangeOptions.map((r) => (
          <Button
            key={r}
            size="sm"
            variant={range === r ? "default" : "outline"}
            onClick={() => setRangeAndLoad(r)}
            data-testid={`defa-range-${r}`}
          >
            {r} {view === "weekly" ? "veckor" : "dagar"}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)]">
          <div className="text-xs text-[color:var(--text-secondary)]">Totalt</div>
          <div className="text-2xl font-semibold" data-testid="defa-total-kwh">
            {total != null ? `${total.toFixed(2)} kWh` : "—"}
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)]">
          <div className="text-xs text-[color:var(--text-secondary)]">{avgLabel}</div>
          <div className="text-2xl font-semibold" data-testid="defa-avg-kwh">
            {avg != null ? `${avg.toFixed(2)} kWh` : "—"}
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Laddar…
        </div>
      )}

      {!loading && (chartData.length === 0 || (total ?? 0) === 0) && (
        <div className="p-6 text-center text-[color:var(--text-secondary)] rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)]">
          Ingen förbrukning registrerad än — historik byggs upp med 5-min-pollning.
          Vänta 1-2 dygn för första graf.
        </div>
      )}

      {!loading && chartData.length > 0 && (total ?? 0) > 0 && (
        <div
          className="p-4 rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)]"
          data-testid="defa-chart"
          style={{ height: 320 }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 8, left: -12, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="x"
                tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                interval={chartData.length > 15 ? "preserveStartEnd" : 0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--text-secondary)" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}`}
                width={44}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-elevated)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value) => [`${Number(value).toFixed(2)} kWh`, "Förbrukning"]}
                labelStyle={{ color: "var(--text-primary)" }}
              />
              <Bar dataKey="kwh" radius={[6, 6, 0, 0]}>
                {chartData.map((d) => (
                  <Cell
                    key={d.key}
                    fill={d.kwh >= maxKwh * 0.9 ? "var(--sunset, #F97316)" : "var(--moss, #4A7C59)"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === "daily" && !loading && (daily?.daily || []).length > 0 && (
        <details className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg-elevated)]">
          <summary className="p-3 cursor-pointer text-sm font-medium">
            Alla dagar ({daily.daily.length})
          </summary>
          <div className="px-3 pb-3 space-y-1 max-h-80 overflow-y-auto">
            {[...daily.daily].reverse().map((d) => (
              <div key={d.date} className="flex justify-between text-sm py-1 border-t border-[color:var(--border)]">
                <span>{d.date}</span>
                <span className="font-semibold">{d.kwh.toFixed(2)} kWh</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// --- Huvudsida ---
export default function Laddaren() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [chargers, setChargers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api.get("/defa/status");
      setStatus(s.data);
      if (s.data.connected) {
        try {
          const c = await api.get("/defa/chargers");
          setChargers(c.data.chargers || []);
        } catch (e) {
          if (e?.response?.status === 401) {
            toast.error("DEFA-token har utgått — anslut igen");
            setStatus({ ...s.data, connected: false });
          } else {
            toast.error(e?.response?.data?.detail || "Kunde inte hämta chargers");
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const c = await api.get("/defa/chargers");
      setChargers(c.data.chargers || []);
      toast.success("Uppdaterat!");
    } catch (e) {
      toast.error("Kunde inte uppdatera");
    } finally {
      setRefreshing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Koppla ur DEFA Power-integrationen?")) return;
    try {
      await api.delete("/defa/setup");
      toast.success("Frånkopplad");
      setStatus({ connected: false });
      setChargers(null);
    } catch (e) {
      toast.error("Kunde inte koppla ur");
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh var 30:e sek när ansluten
    const t = setInterval(() => {
      if (status?.connected) refresh();
    }, 30000);
    return () => clearInterval(t);
     
  }, []);

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] text-[color:var(--text-primary)]">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/")}
            data-testid="defa-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Tillbaka
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Zap className="w-6 h-6 text-[color:var(--sunset)]" />
              Laddaren
            </h1>
            <p className="text-xs text-[color:var(--text-secondary)]">
              DEFA Power — statistik & live-status
            </p>
          </div>
          {status?.connected && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={refreshing}
                data-testid="defa-refresh-btn"
              >
                {refreshing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={disconnect}
                data-testid="defa-disconnect-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[color:var(--text-secondary)]" />
          </div>
        ) : !status?.connected ? (
          <div className="p-6 rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)]">
            <h2 className="text-lg font-semibold mb-2">Anslut din DEFA Power-laddare</h2>
            <p className="text-sm text-[color:var(--text-secondary)] mb-4">
              Vi kopplar upp mot CloudCharge-API (samma som DEFA Power-appen) för
              att läsa av statistik och live-status. Vi lagrar bara krypterad token.
            </p>
            <DefaSetup onDone={load} />
          </div>
        ) : (
          <>
            {status.last_error && (
              <div className="p-3 rounded-xl bg-[color:var(--danger-bg,#FEE2E2)] text-[color:var(--danger,#B91C1C)] text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Senaste fel: {status.last_error}
              </div>
            )}

            <div className="text-xs text-[color:var(--text-secondary)] text-right">
              Senast synkad: {fmtDateTime(status.last_sync)}
            </div>

            {(chargers || []).length === 0 ? (
              <div className="p-6 text-center text-[color:var(--text-secondary)] rounded-2xl bg-[color:var(--bg-elevated)] border border-[color:var(--border)]">
                Inga chargers hittade på ditt konto.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2" data-testid="defa-chargers-grid">
                {chargers.map((cp) => (
                  <ChargerCard key={cp.chargepoint_id} charger={cp} />
                ))}
              </div>
            )}

            <div className="pt-6 border-t border-[color:var(--border)]">
              <HistorySection />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
